import { useQuery } from '@tanstack/react-query';

import React from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';

import { submissionKey } from '@dashboard/lib/queryKeys';

import { fetcher } from '@dashboard/services/request';
import getSubmissionStatusLabel from '@dashboard/services/status/getSubmissionStatusLabel';

// Import scene components using relative paths
import SubmissionsDetail, { formatSubmissionCreated } from './scenes/SubmissionsDetail';
import { OrderPageHeader, OrderPageSkeleton } from '@dashboard/components/OrderPage';
import SubmissionsDetailEmailLogs from './scenes/SubmissionsDetailEmailLogs';

// Import necessary UI components
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import NavTabs from '@dashboard/components/NavTabs';
import StatusBadge from '@dashboard/components/StatusBadge';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import { getPageTitle } from '@dashboard/lib/pageTitle';

// Define the tabs (pass only submissionId, submission)
const getTabs = ({ base, submissionId, submission }) => [
  {
    name: 'Details',
    href: `${base}`,
    render: props => <SubmissionsDetail {...props} submission={submission} />,
  },
  {
    name: 'Emails',
    href: `${base}/emails`,
    render: props => <SubmissionsDetailEmailLogs {...props} submission={submission} />,
  },
];

const SubmissionsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const {
    url,
    params: { submissionId },
  } = useRouteMatch();
  const location = useLocation();

  const {
    data: submission,
    isError,
    isLoading,
  } = useQuery({
    queryKey: submissionKey(submissionId),
    queryFn: () => fetcher(`/sellers/${sellerId}/submissions/${submissionId}`, token),
    enabled: !!submissionId,
  });

  React.useEffect(() => {
    if (submission?.seller) {
      setAdminGhostingSellerId(submission.seller);
    }
  }, [submission?.seller, setAdminGhostingSellerId]);

  if (isLoading) {
    return <OrderPageSkeleton tabs={2} />;
  }

  if (!submission || isError) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  // Pass only necessary props
  const tabs = getTabs({
    base: url,
    submissionId,
    submission,
  });
  const status = submission.orderStatus === 'canceled' ? 'canceled' : submission.status;

  return (
    <>
      <TrackRecent
        type="submission"
        id={submission._id}
        label={getPageTitle(submission.page) || submission._id}
        subtitle={submission.customer?.email}
        path={`/submissions/${submission._id}`}
      />
      <Card variant="root" contentColumn>
        <CardHeader variant="root">
          {/* Submissions have no order number yet (carded as API work), so the title is the record's kind. */}
          <OrderPageHeader
            title="Submission"
            badges={
              <StatusBadge
                status={status}
                livemode={submission.livemode}
                label={getSubmissionStatusLabel(status)}
              />
            }
            date={formatSubmissionCreated(submission.createdAt)}
          />
          <NavTabs aria-label="Submission detail">
            {tabs.map(tab => (
              <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
                {tab.name}
              </NavTabs.Item>
            ))}
          </NavTabs>
        </CardHeader>
        <CardContent variant="compact">
          <Switch>
            {tabs.map(tab => (
              <Route key={tab.href} path={tab.href} render={props => tab.render(props)} exact />
            ))}
          </Switch>
        </CardContent>
      </Card>
    </>
  );
};

export default SubmissionsDetailOverview;
